import { faDash } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal } from 'atoms/layout/flex';
import { BrandXS } from 'atoms/typography';
import { absCenter, unitMapping } from 'lib/style';
import type { Color } from 'style/types';

type Props = {
  value: number | null;
  size: keyof typeof unitMapping;
};

const Root = styled(Horizontal).attrs({ gap: 0, center: true })`
  --score-color: var(--c-white);
  position: relative;
`;

const Score = styled(BrandXS)`
  color: var(--score-color);
  ${absCenter}

  font: var(--t-label-m);
  font-family: var(--brand-font);
  .small & {
    font: var(--t-label-s);
    font-family: var(--brand-font);
  }
  .smaller & {
    font-size: var(--unit);
    font-family: var(--brand-font);
  }

  @supports (paint-order: stroke fill) {
    text-shadow: none;
    paint-order: stroke fill;
    -webkit-text-stroke: 3px var(--c-black);
    .smaller & {
      -webkit-text-stroke: 2px var(--c-black);
    }
  }
`;

const getGainColor = (value: number): Color => {
  if (value > 5) return 'var(--c-score-mediumHigh)';
  if (value >= 1) return 'var(--c-score-medium)';
  if (value > -1) return 'var(--c-white)';
  if (value > -5) return 'var(--c-score-low)';
  return 'var(--c-score-veryLow)';
};

export const Gain = ({ value, size = 4 }: Props) => {
  if (value === null) {
    return <FontAwesomeIcon icon={faDash} color="var(--c-nd-500)" />;
  }
  const roundedValue = Math.round(value);

  const color = getGainColor(roundedValue);

  const chevronDownColor = roundedValue > 0 ? color : 'var(--c-nd-600)';
  const chevronUpColor = roundedValue < 0 ? color : 'var(--c-nd-600)';

  return (
    <Root
      className={classNames({
        small: size <= 3,
        smaller: size <= 2,
      })}
      style={{ height: unitMapping[size], width: unitMapping[size] }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 27 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M21.1939 22.4144L13.7797 29.8286L6.3655 22.4144L9.19393 19.5859L13.7797 24.1717L18.3655 19.5859L21.1939 22.4144Z"
          fill={chevronUpColor}
        />
        <path
          d="M18.7893 19.1621L21.6184 21.9902L22.0423 22.4141L21.6184 22.8389L14.2044 30.2529L13.7796 30.6768L5.51687 22.4141L5.94168 21.9902L8.7698 19.1621L9.19363 18.7373L9.61843 19.1621L13.7796 23.3223L17.9417 19.1621L18.3655 18.7373L18.7893 19.1621Z"
          stroke="var(--c-white)"
          strokeOpacity="0.07"
          strokeWidth="1.2"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.7797 7.82873L18.3654 12.4145L21.1939 9.58609L13.7797 2.17187L6.36545 9.58609L9.19387 12.4145L13.7797 7.82873Z"
          fill={chevronDownColor}
        />
        <path
          d="M18.3657 13.2627L17.9409 12.8389L13.7798 8.67676L9.19387 13.2627L8.77005 12.8389L5.94095 10.0107L5.51712 9.58594L13.7798 1.32324L14.2036 1.74805L21.6177 9.16211L22.0425 9.58594L18.3657 13.2627Z"
          stroke="var(--c-white)"
          strokeOpacity="0.07"
          strokeWidth="1.2"
        />
      </svg>

      <Score
        className="text-center"
        style={{ '--score-color': color } as CSSProperties}
      >
        <FormattedNumber
          value={value}
          maximumFractionDigits={0}
          signDisplay="exceptZero"
        />
      </Score>
    </Root>
  );
};
